import { useEffect, useState } from "react";
import { toast } from "sonner";

import { api, formatApiError } from "@/lib/api";

function TrainerSelfie({ id }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let url = "";

    api
      .get(`/attendance/trainer-selfies/${id}`, { responseType: "blob" })
      .then((response) => {
        url = URL.createObjectURL(response.data);
        setSrc(url);
      })
      .catch(() => setSrc(""));

    return () => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [id]);

  return src ? <img className="attendance-thumb" src={src} alt="Trainer attendance selfie" /> : "✓";
}

export default function TrainerAttendanceReport({ centers = [], fixedCenterId = "" }) {
  const [centerId, setCenterId] = useState(fixedCenterId);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (fixedCenterId) {
      setCenterId(fixedCenterId);
    }
  }, [fixedCenterId]);

  useEffect(() => {
    if (!centerId) {
      setData(null);
      return;
    }

    api
      .get(`/attendance/trainers/monthly?month=${month}&center_id=${centerId}`)
      .then((response) => setData(response.data))
      .catch(() => setData(null));
  }, [centerId, month]);

  const days = new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0).getDate();
  const records = new Map(
    (data?.attendance || []).map((item) => [
      `${item.trainer_id}-${Number(item.attendance_date.slice(-2))}`,
      item,
    ]),
  );

  const download = async () => {
    setIsDownloading(true);
    try {
      const response = await api.get(
        `/attendance/trainers/monthly/export?month=${month}&center_id=${centerId}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `trainer_attendance_${month}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Trainer attendance CSV downloaded.");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <section className="attendance-report" id="attendance" data-testid="trainer-attendance-report">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Trainer attendance</p>
          <h2>Monthly trainer check-ins</h2>
        </div>
        <div className="attendance-report-controls">
          {!fixedCenterId && (
            <select
              value={centerId}
              onChange={(event) => setCenterId(event.target.value)}
              data-testid="trainer-attendance-center-picker"
            >
              <option value="">Select center</option>
              {centers.map((center) => (
                <option key={center.id} value={center.id}>
                  {center.name}
                </option>
              ))}
            </select>
          )}
          <input
            type="month"
            value={month}
            onChange={(event) => setMonth(event.target.value)}
            data-testid="trainer-attendance-month-picker"
          />
          <button
            className="outline-button"
            onClick={download}
            disabled={!centerId || isDownloading}
            data-testid="download-trainer-attendance-csv-button"
          >
            {isDownloading ? "Preparing CSV" : "Download CSV"}
          </button>
        </div>
      </div>
      {data && (
        data.trainers.length ? (
          <div className="attendance-report-table-wrap">
            <table data-testid="trainer-attendance-table">
              <thead>
                <tr>
                  <th>Trainer</th>
                  {Array.from({ length: days }, (_, index) => (
                    <th key={index}>{index + 1}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.trainers.map((trainer) => (
                  <tr key={trainer.id} data-testid={`trainer-attendance-row-${trainer.id}`}>
                    <td>{trainer.name}</td>
                    {Array.from({ length: days }, (_, index) => {
                      const record = records.get(`${trainer.id}-${index + 1}`);
                      return <td key={index}>{record ? <TrainerSelfie id={record.id} /> : "—"}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="attendance-empty-state" data-testid="trainer-attendance-empty-state">
            No trainers are mapped to this center yet. Assign trainers to the center before reviewing
            attendance.
          </div>
        )
      )}
    </section>
  );
}